import { createSelector } from "reselect";
import { Map, Record as ImmutableRecord } from "immutable";
import { addDays, subDays, format, parseISO } from "date-fns";

import {
  EXTRA_SERVICES_BOOKING,
  CALENDAR_WEEK_SHIFT,
  CLEANING_FREQUENCY,
  Task,
  AGENT_PREFERENCE,
  PricePlan,
} from "@typings/booking";
import { AVAILABILITIES_CHANGE_TYPE } from "@typings/bookingCalendarV3";

import mapToList from "@services/MapToList";
import { DATE_FORMAT, newDateSafe } from "@services/DateFormatter";
import { intlNumberFormatCurrency } from "@services/IntlNumberFormatCurrency";

import { IStore } from "@app/redux/reducers";

import { TelemetryCalendarContext } from "./booking.typings";

const getTasksFunction = (store: IStore) => Map(store.booking.get("tasks"));

export const getTasks = createSelector(getTasksFunction, f => f);

const getTaskFunction = (store: IStore, service: EXTRA_SERVICES_BOOKING) =>
  store.booking.getIn(["tasks", service]) as ImmutableRecord<Task>;

export const getTask = createSelector(getTaskFunction, f => f);

const getRoomQuantityFunction = (
  store: IStore,
  roomType: "bedrooms" | "bathrooms",
) => getTask(store, EXTRA_SERVICES_BOOKING.HOME_CLEANING).get(roomType);

export const getRoomQuantity = createSelector(getRoomQuantityFunction, f => f);

const getSelectedTasksListFunction = (store: IStore) =>
  mapToList(getTasks(store)).filter(item => item.get("selected"));

export const getSelectedTasksList = createSelector(
  getSelectedTasksListFunction,
  f => f,
);

const getSelectedTasksDurationFunction = (store: IStore) =>
  getTasks(store).reduce(
    (duration, task) =>
      duration + (task.get("selected") ? task.get("duration") : 0),
    0,
  );

export const getSelectedTasksDuration = createSelector(
  getSelectedTasksDurationFunction,
  f => f,
);

const getPricingFunction = (store: IStore) => store.booking.get("pricing");

/**
 * Select standard pricing from `/pricing` endpoint
 */
export const getPricing = createSelector(getPricingFunction, f => f);

const getSelectedCleaningFrequencyFunction = (store: IStore) =>
  store.booking.get("frequency");

export const getSelectedCleaningFrequency = createSelector(
  getSelectedCleaningFrequencyFunction,
  f => f,
);

const getAllPricingPlansFunction = (store: IStore) =>
  store.booking.get("pricePlans");

/**
 * Select all plans
 */
export const getAllPricingPlans = createSelector(
  getAllPricingPlansFunction,
  f => f,
);

/**
 * Select available plans for current booking, considering selected frequency
 * and other conditions
 */
export const getAvailablePricingPlans = createSelector(
  [getAllPricingPlansFunction, getSelectedCleaningFrequencyFunction],
  (plans, frequency) =>
    plans.filter(
      plan => plan.get("frequency") === frequency && !plan.get("hidden"),
    ),
);

const getSelectedPricePlanUuidFunction = (store: IStore) =>
  store.booking.get("pricePlanUuid");

export const getSelectedPricePlanUuid = createSelector(
  getSelectedPricePlanUuidFunction,
  f => f,
);

export const getSelectedPricePlanName = createSelector(
  getSelectedCleaningFrequencyFunction,
  getAllPricingPlansFunction,
  (selectedFrequency, allPlans) =>
    allPlans
      .find(plan => plan.get("frequency") === selectedFrequency)
      ?.get("name"),
);

export const getSelectedPricePlan = createSelector(
  getSelectedCleaningFrequencyFunction,
  getAllPricingPlansFunction,
  (selectedFrequency, allPlans) =>
    allPlans.find(plan => plan.get("frequency") === selectedFrequency),
);

/**
 * getTotalCleaningTimeFunction
 * @param exludedTasks - Array with extra services/tasks excluded from calculating total time.
 */
const getTotalCleaningTimeFunction = (
  store: IStore,
  exludedTasks: EXTRA_SERVICES_BOOKING[],
) =>
  getTasks(store)
    .valueSeq()
    .filter(task => {
      if (!Array.isArray(exludedTasks)) return task.get("selected");

      return (
        task.get("selected") && !exludedTasks.includes(task.get("service"))
      );
    })
    .reduce((acc: number, task) => acc + task.get("duration"), 0);

export const getTotalCleaningTime = createSelector(
  getTotalCleaningTimeFunction,
  f => f,
);

const getAgentPreferenceFunction = (store: IStore) =>
  store.booking.get("agentPreference");

export const getAgentPreference = createSelector(
  getAgentPreferenceFunction,
  f => f,
);

const getServiceLocationIdFunction = (store: IStore) =>
  store.booking.get("serviceLocationId");

export const getServiceLocationId = createSelector(
  getServiceLocationIdFunction,
  f => f,
);

const getHasPetsFunction = (store: IStore) => store.booking.get("hasPets");

export const getHasPets = createSelector(getHasPetsFunction, f => f);

const getPetsDetailsFunction = (store: IStore) =>
  store.booking.get("petsDetails");

export const getPetsDetails = createSelector(getPetsDetailsFunction, f => f);

const getPreviousAgentsFunction = (store: IStore) =>
  store.booking.get("previousAgents");

export const getPreviousAgents = createSelector(
  getPreviousAgentsFunction,
  f => f,
);

const getPreviousAgentsLoadingStateFn = (store: IStore) =>
  store.booking.get("previousAgentsLoadingState");

export const getPreviousAgentsLoadingState = createSelector(
  getPreviousAgentsLoadingStateFn,
  f => f,
);

const getAgentByUuidFunction = (store: IStore, uuid: string) => {
  const result = getPreviousAgents(store).findEntry(
    item => item.get("uuid") === uuid,
  );
  return result ? result[1] : undefined;
};

export const getAgentByUuid = createSelector(getAgentByUuidFunction, f => f);

const getIsChoosingAnyBatmaidFunction = (store: IStore) =>
  store.booking.get("isChoosingAnyBatmaid");

export const getIsChoosingAnyBatmaid = createSelector(
  getIsChoosingAnyBatmaidFunction,
  f => f,
);

const getCalendarWeekFunction = (store: IStore, week: CALENDAR_WEEK_SHIFT) => {
  const weekStartDate = store.booking.getIn(["calendar", "weekStartDate"]);
  const currentWeek = parseISO(weekStartDate);

  return week === CALENDAR_WEEK_SHIFT.PREVIOUS
    ? format(subDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY)
    : format(addDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY);
};

export const getCalendarWeek = createSelector(getCalendarWeekFunction, f => f);

const getVoucherFunction = (store: IStore) => store.booking.get("voucherToken");

export const getVoucher = createSelector(getVoucherFunction, f => f);

const getKeyPickFunction = (store: IStore) => store.booking.get("keyPick");

export const getKeyPick = createSelector(getKeyPickFunction, f => f);

const getLocationCommentFunction = (store: IStore) =>
  store.booking.get("locationComment");

export const getLocationComment = createSelector(
  getLocationCommentFunction,
  f => f,
);

const getPreselectedAgentUuidFunction = (store: IStore) =>
  store.booking.get("preselectedAgentUuid");

export const getPreselectedAgentUuid = createSelector(
  getPreselectedAgentUuidFunction,
  f => f,
);

const getReservationUuidFunction = (store: IStore) =>
  store.booking.get("reservationUuid");

export const getReservationUuid = createSelector(
  getReservationUuidFunction,
  f => f,
);

const getSpecialInstructionsFunction = (store: IStore) =>
  store.booking.get("specialInstructions");

export const getSpecialInstructions = createSelector(
  getSpecialInstructionsFunction,
  f => f,
);

const getClientRegistrationFormFunction = (store: IStore) =>
  store.booking.get("form");

export const getClientRegistrationForm = createSelector(
  getClientRegistrationFormFunction,
  f => f,
);

const getAvailablePaymentMethodsFunction = (store: IStore) =>
  store.booking.get("availablePaymentMethods");

export const getAvailablePaymentMethods = createSelector(
  getAvailablePaymentMethodsFunction,
  f => f,
);

const getSelectedPaymentMethodFunction = (store: IStore) =>
  store.booking.get("selectedPaymentMethod");

export const getSelectedPaymentMethod = createSelector(
  getSelectedPaymentMethodFunction,
  f => f,
);

const getMainLocationUuidFunction = (store: IStore) =>
  store.booking.get("mainLocationUuid");

export const getMainLocationUuid = createSelector(
  getMainLocationUuidFunction,
  f => f,
);

const getLocationUuidFunction = (store: IStore) =>
  store.booking.get("locationUuid");

export const getLocationUuid = createSelector(getLocationUuidFunction, f => f);

const getTemplateJobIdFunction = (store: IStore) =>
  store.booking.get("templateJobId");

export const getTemplateJobId = createSelector(
  getTemplateJobIdFunction,
  f => f,
);

const getQueryParamsFunction = (store: IStore) =>
  store.booking.get("queryParams");

export const getQueryParams = createSelector(getQueryParamsFunction, f => f);

const getVoucherMessageFunction = (store: IStore) =>
  store.booking.get("voucherMessage");

export const getVoucherMessage = createSelector(
  getVoucherMessageFunction,
  f => f,
);

const getVoucherStatusFunction = (store: IStore) =>
  store.booking.get("voucherStatus");

export const getVoucherStatus = createSelector(
  getVoucherStatusFunction,
  f => f,
);

const getCleaningProductsFunction = (store: IStore) =>
  store.booking.get("cleaningProducts");

export const getCleaningProducts = createSelector(
  getCleaningProductsFunction,
  f => f,
);

const getSelectedCleaningProductsIdsFunction = (store: IStore) =>
  store.booking.get("selectedCleaningProductsIds");

export const getSelectedCleaningProductsIds = createSelector(
  getSelectedCleaningProductsIdsFunction,
  f => f,
);

const getNonStandardFrequencyShiftsFunction = (store: IStore) =>
  store.booking.get("nonStandardFrequencyShifts");

export const getNonStandardFrequencyShifts = createSelector(
  getNonStandardFrequencyShiftsFunction,
  f => f,
);

const getTotalDurationOfMoreOftenFrequencyShiftsFunction = (store: IStore) =>
  store.booking
    .get("nonStandardFrequencyShifts")
    .reduce((acc, item) => acc + Number(item.duration), 0);

export const getTotalDurationOfMoreOftenFrequencyShifts = createSelector(
  getTotalDurationOfMoreOftenFrequencyShiftsFunction,
  f => f,
);

const getContactAfterBookingFunction = (store: IStore) =>
  store.booking.get("contactAfterBooking");

export const getContactAfterBooking = createSelector(
  getContactAfterBookingFunction,
  f => f,
);

const getHasServiceVouchersFunction = (store: IStore) =>
  store.booking.get("hasServiceVouchers");

export const getHasServiceVouchers = createSelector(
  getHasServiceVouchersFunction,
  f => f,
);

const getServiceVoucherPlansFunction = (store: IStore) =>
  store.booking.get("serviceVoucherPlans");

export const getServiceVoucherPlans = createSelector(
  getServiceVoucherPlansFunction,
  f => f,
);

const getTotalCleaningTimeMoreOftenFn = (store: IStore) => {
  const shiftsCount = store.booking.get("nonStandardFrequencyShifts").length;
  const shiftsDuration = getTotalDurationOfMoreOftenFrequencyShifts(store);
  const extrasDuration = getTasks(store)
    .valueSeq()
    .filter(
      task =>
        task.get("selected") &&
        task.get("service") !== EXTRA_SERVICES_BOOKING.HOME_CLEANING,
    )
    .reduce((acc: number, task) => acc + task.get("duration"), 0);

  /* eslint-disable no-mixed-operators */
  return extrasDuration * shiftsCount + shiftsDuration;
};

export const getTotalCleaningTimeMoreOften = createSelector(
  getTotalCleaningTimeMoreOftenFn,
  f => f,
);

const getStandardPriceForFrequencyFn =
  (frequency: CLEANING_FREQUENCY) => (store: IStore) => {
    const standardPricing = getPricing(store);
    const priceForFrequency = standardPricing
      .get("pricesPerFrequency")
      .find(item => item.frequency === frequency);

    return priceForFrequency?.amount || null;
  };

export const getStandardPriceForFrequency = (frequency: CLEANING_FREQUENCY) =>
  createSelector(getStandardPriceForFrequencyFn(frequency), f => f);

const getHasUrssafFunction = (store: IStore) => store.booking.get("hasUrssaf");

export const getHasUrssaf = createSelector(getHasUrssafFunction, f => f);

const getPricingLoadingStateFn = (store: IStore) =>
  store.booking.get("pricingLoadingState");

export const getPricingLoadingState = createSelector(
  getPricingLoadingStateFn,
  f => f,
);

const getPaymentMethodsLoadingStateFn = (store: IStore) =>
  store.booking.get("paymentMethodsLoadingState");

export const getPaymentMethodsLoadingState = createSelector(
  getPaymentMethodsLoadingStateFn,
  f => f,
);

const getServiceVouchersLoadingStateFn = (store: IStore) =>
  store.booking.get("serviceVouchersLoadingState");

export const getServiceVouchersLoadingState = createSelector(
  getServiceVouchersLoadingStateFn,
  f => f,
);

const getFrequenciesFn = (store: IStore) => store.booking.get("frequencies");

export const getFrequencies = createSelector(getFrequenciesFn, f => f);

const getFrequenciesLoadingStateFn = (store: IStore) =>
  store.booking.get("frequenciesLoadingState");

export const getFrequenciesLoadingState = createSelector(
  getFrequenciesLoadingStateFn,
  f => f,
);

const getPricePlansLoadingStateFn = (store: IStore) =>
  store.booking.get("pricePlansLoadingState");

export const getPricePlansLoadingState = createSelector(
  getPricePlansLoadingStateFn,
  f => f,
);

const getPriceWillChangeFn = (store: IStore) =>
  store.booking.get("priceWillChange");

export const getPriceWillChange = createSelector(getPriceWillChangeFn, f => f);

const getSurveyBookingContextFn = (store: IStore) => {
  const selectedTasks = getSelectedTasksListFunction(store);
  const agentPreference = getAgentPreference(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const useUrssaf = getHasUrssaf(store);

  return {
    serviceLocationId: store.booking.get("serviceLocationId"),
    executionDateTime: getExecutionDate(store),
    hasPets: store.booking.get("hasPets"),
    pricePlanUuid: store.booking.get("pricePlanUuid"),
    frequency: store.booking.get("frequency"),
    paymentMethod: store.booking.get("selectedPaymentMethod"),
    voucherToken: store.booking.get("voucherToken"),
    selectedTasks,
    ...(useUrssaf && { useUrssaf }),
    ...(preselectedAgentUuid &&
      agentPreference === AGENT_PREFERENCE.PRESELECTED && {
        preselectedAgentUuid,
      }),
  };
};

export const getSurveyBookingContext = createSelector(
  getSurveyBookingContextFn,
  f => f,
);

const getTelemetryBookingContextFn = (store: IStore) => {
  const selectedTasks = getSelectedTasksListFunction(store);
  const agentPreference = getAgentPreference(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const useUrssaf = getHasUrssaf(store);

  return {
    serviceLocationId: store.booking.get("serviceLocationId"),
    executionDateTime: getExecutionDate(store),
    hasPets: store.booking.get("hasPets"),
    pricePlanUuid: store.booking.get("pricePlanUuid"),
    frequency: store.booking.get("frequency"),
    paymentMethod: store.booking.get("selectedPaymentMethod"),
    voucherToken: store.booking.get("voucherToken"),
    selectedTasks,
    ...(useUrssaf && { useUrssaf }),
    ...(preselectedAgentUuid &&
      agentPreference === AGENT_PREFERENCE.PRESELECTED && {
        preselectedAgentUuid,
      }),
  };
};

export const getTelemetryBookingContext = createSelector(
  getTelemetryBookingContextFn,
  f => f,
);

type PricesForFrequencies = Partial<
  Record<
    CLEANING_FREQUENCY,
    {
      uuid: string;
      price: string;
    }
  >
>;

const getPricesForFrequenciesSelectorFn = (
  store: IStore,
): PricesForFrequencies => {
  const pricing = store.booking.get("pricePlans");
  const data: PricesForFrequencies = {};

  pricing
    .toJS()
    .map(({ frequency, uuid, pricePerHour, currency }: PricePlan) => {
      data[frequency as CLEANING_FREQUENCY] = {
        uuid,
        price: intlNumberFormatCurrency(pricePerHour, currency),
      };
    });

  return data;
};

export const getPricesForFrequenciesSelector = createSelector(
  getPricesForFrequenciesSelectorFn,
  f => f,
);

const getNextPeriodStartDateFn = (
  store: IStore,
  changeType: AVAILABILITIES_CHANGE_TYPE,
  isTouched: boolean,
): string | null => {
  const calendar = getCalendar(store);

  if (changeType === AVAILABILITIES_CHANGE_TYPE.NEXT_PERIOD) {
    return calendar.get("nextStartDate");
  }

  if (changeType === AVAILABILITIES_CHANGE_TYPE.PREVIOUS_PERIOD) {
    return calendar.get("previousStartDate");
  }

  if (changeType === AVAILABILITIES_CHANGE_TYPE.FACTORIES && isTouched) {
    return calendar.get("period").startDate;
  }

  return null;
};

export const getNextPeriodStartDate = createSelector(
  getNextPeriodStartDateFn,
  f => f,
);

const getAvailabilitiesStateFn = (store: IStore) =>
  store.booking.get("calendar").get("state");

export const getAvailabilitiesState = createSelector(
  getAvailabilitiesStateFn,
  f => f,
);

const getCalendarFn = (store: IStore) => store.booking.get("calendar");

export const getCalendar = createSelector(getCalendarFn, f => f);

const getExecutionFn = (store: IStore) => store.booking.get("execution");

export const getExecution = createSelector(getExecutionFn, f => f);

const getExecutionDateFn = (store: IStore) => {
  const execution = store.booking.get("execution");

  if (!execution.date || !execution.time) {
    return null;
  }

  return `${execution.date}T${execution.time}`;
};

export const getExecutionDate = createSelector(getExecutionDateFn, f => f);

const getIsTouchedCalendarFn = (store: IStore) =>
  store.booking.get("calendar").get("isTouched");

export const getIsTouchedCalendar = createSelector(
  getIsTouchedCalendarFn,
  f => f,
);

const getHasAvailabilityInPlaningWindowFn = (store: IStore) =>
  store.booking.get("calendar").get("hasAvailabilityInPlaningWindow");

export const getHasAvailabilityInPlaningWindow = createSelector(
  getHasAvailabilityInPlaningWindowFn,
  f => f,
);

const getHasAvailabilityInCurrentPeriodFn = (store: IStore) =>
  store.booking.get("calendar").get("hasAvailabilityInCurrentPeriod");

export const getHasAvailabilityInCurrentPeriod = createSelector(
  getHasAvailabilityInCurrentPeriodFn,
  f => f,
);

const getIsCleaningChecklistAvailableFn = (store: IStore) =>
  store.booking.get("isCleaningChecklistAvailable");

export const getIsCleaningChecklistAvailable = createSelector(
  getIsCleaningChecklistAvailableFn,
  f => f,
);

const getTelemetryCalendarContextFn = (
  store: IStore,
): TelemetryCalendarContext => {
  const execution = getExecution(store);
  const frequency = getSelectedCleaningFrequency(store);
  const duration =
    frequency === CLEANING_FREQUENCY.MORE_OFTEN
      ? getTotalCleaningTimeMoreOften(store)
      : getTotalCleaningTime(store, []);

  if (!execution.date) {
    return {
      frequency,
      duration,
      displayedSlots: [],
    };
  }

  const availability = getCalendar(store);
  const hoursList = availability.get("hours")[execution.date];

  const displayedSlots = hoursList
    ? hoursList.reduce((acc: string[], item) => {
        if (item.availableAgentsRemaining) {
          return [...acc, `${execution.date} ${item.time}`];
        }

        return acc;
      }, [])
    : [];

  return {
    frequency,
    duration,
    displayedSlots,
  };
};

export const getTelemetryCalendarContext = createSelector(
  getTelemetryCalendarContextFn,
  f => f,
);

const getCalendarDependenciesFn = (store: IStore) => {
  const tasks = getTasks(store);
  const frequency = getSelectedCleaningFrequency(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const reservationUuid = getReservationUuid(store);
  const hasPets = getHasPets(store);
  const agentPreference = getAgentPreference(store);
  const totalCleaningTime = getTotalCleaningTime(store, []);
  const hasServiceVouchers = getHasServiceVouchers(store);
  const hasUrssaf = getHasUrssaf(store);

  return [
    JSON.stringify(tasks),
    frequency,
    preselectedAgentUuid,
    reservationUuid,
    hasPets,
    agentPreference,
    totalCleaningTime,
    hasServiceVouchers,
    hasUrssaf,
  ];
};

export const getCalendarDependencies = createSelector(
  getCalendarDependenciesFn,
  f => f,
);
